import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConservationPill from "@/components/ConservationPill";
import { StarRating } from "@/components/StarRating";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Filter, Bell } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Marketplace() {
  const [lots, setLots] = useState([]);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", species: "all", conservation_status: "all", lot_type: "all" });
  const { user } = useAuth();
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.species !== "all") params.species = filters.species;
    if (filters.conservation_status !== "all") params.conservation_status = filters.conservation_status;
    if (filters.lot_type !== "all") params.lot_type = filters.lot_type;
    const { data } = await api.get("/lots", { params });
    setLots(data.lots);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const saveSearch = async () => {
    try {
      const f = {};
      if (filters.q) f.q = filters.q;
      if (filters.species !== "all") f.species = filters.species;
      if (filters.conservation_status !== "all") f.conservation_status = filters.conservation_status;
      if (filters.lot_type !== "all") f.lot_type = filters.lot_type;
      await api.post("/saved-searches", { name: saveName, filters: f, alert: true });
      toast.success("Search saved — you'll be emailed when matching lots are listed");
      setSaveOpen(false); setSaveName("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="heading text-3xl sm:text-4xl font-extrabold tracking-tight">{t("mp.title")}</h1>
          <p className="text-stone-600 mt-1">{t("mp.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-stone-500">
          {user && <Button variant="outline" size="sm" data-testid="mp-save-search-btn" onClick={() => setSaveOpen(true)}><Bell className="h-4 w-4 mr-1" /> Save search &amp; get alerts</Button>}
          <Filter className="h-4 w-4" /><span className="mono">{lots.length} {t("mp.lots")}</span>
        </div>
      </div>

      <div className="mt-6 surface-card p-4 grid md:grid-cols-4 gap-3">
        <div className="relative md:col-span-1">
          <Search className="h-4 w-4 absolute left-3 top-3 text-stone-400" />
          <Input data-testid="mp-search-input" className="pl-9" placeholder={t("mp.search")} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
        <Select value={filters.species} onValueChange={(v) => setFilters({ ...filters, species: v })}>
          <SelectTrigger data-testid="mp-species-filter"><SelectValue placeholder="Species" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("mp.all_species")}</SelectItem>
            <SelectItem value="cattle">Cattle</SelectItem>
            <SelectItem value="equine">Equine</SelectItem>
            <SelectItem value="sheep">Sheep</SelectItem>
            <SelectItem value="swine">Swine</SelectItem>
            <SelectItem value="goat">Goat</SelectItem>
            <SelectItem value="canine">Canine</SelectItem>
            <SelectItem value="camel">Camel</SelectItem>
            <SelectItem value="poultry">Poultry</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.conservation_status} onValueChange={(v) => setFilters({ ...filters, conservation_status: v })}>
          <SelectTrigger data-testid="mp-status-filter"><SelectValue placeholder="Conservation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("mp.all_statuses")}</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="THREATENED">Threatened</SelectItem>
            <SelectItem value="WATCH">Watch</SelectItem>
            <SelectItem value="RECOVERING">Recovering</SelectItem>
            <SelectItem value="STABLE">Stable</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.lot_type} onValueChange={(v) => setFilters({ ...filters, lot_type: v })}>
          <SelectTrigger data-testid="mp-type-filter"><SelectValue placeholder="Lot type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("mp.all_types")}</SelectItem>
            <SelectItem value="semen_straw">Semen straws</SelectItem>
            <SelectItem value="embryo">Embryos</SelectItem>
            <SelectItem value="oocyte">Oocytes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-stone-500">Loading…</div>
      ) : lots.length === 0 ? (
        <div className="py-16 text-center text-stone-500">{t("mp.none")}</div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {lots.map((lot) => (
            <LotCard key={lot.lot_id} lot={lot} />
          ))}
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-search-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="h-4 w-4 text-[color:var(--earth-amber)]" /> Save this search</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Save the current filters and get email alerts for matching lots.</DialogDescription>
          <div className="text-xs text-stone-500 mono">
            {filters.q && `search: "${filters.q}" · `}
            {filters.species !== "all" && `${filters.species} · `}
            {filters.conservation_status !== "all" && `${filters.conservation_status} · `}
            {filters.lot_type !== "all" && `${filters.lot_type}`}
            {!filters.q && filters.species === "all" && filters.conservation_status === "all" && filters.lot_type === "all" && "All new lots"}
          </div>
          <Input data-testid="save-search-name" placeholder="Name it — e.g. Critical cattle alerts" value={saveName} onChange={(e) => setSaveName(e.target.value)} />
          <Button data-testid="save-search-submit" className="btn-earth" disabled={saveName.trim().length < 2} onClick={saveSearch}>Save &amp; enable email alerts</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LotCard({ lot }) {
  const { fmt } = useCurrency();
  const { t } = useLanguage();
  return (
    <Link to={`/lots/${lot.lot_id}`} data-testid={`lot-card-${lot.lot_id}`} className="surface-card overflow-hidden hover:shadow-xl transition-shadow group">
      <div className="h-44 bg-stone-100 relative">
        {lot.image_url && <img src={lot.image_url} alt={lot.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <ConservationPill status={lot.conservation_status} />
          <span className="pill-stable inline-flex items-center px-2.5 py-1 border rounded-full text-xs uppercase mono tracking-wide">{lot.species}</span>
        </div>
      </div>
      <div className="p-4">
        <div className="text-xs mono text-stone-500">{lot.lot_id}</div>
        <h3 className="heading text-lg font-semibold mt-1 line-clamp-1">{lot.title}</h3>
        <p className="text-sm text-stone-600 mt-1">{lot.breed} · <span className="mono">{lot.donor_name}</span></p>
        {lot.seller_rating_count > 0 && <div className="mt-1.5 flex items-center gap-1.5" data-testid={`lot-seller-rating-${lot.lot_id}`}><StarRating value={lot.seller_rating_avg} size="h-3.5 w-3.5" /><span className="text-xs text-stone-500 mono">{lot.seller_rating_avg?.toFixed(1)} ({lot.seller_rating_count})</span></div>}
        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">{t("mp.price_unit")}</div>
            <div className="heading font-bold mono text-lg text-stone-900">{fmt(lot.price_per_unit)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-stone-400">{t("mp.available")}</div>
            <div className="mono text-sm text-stone-700">{lot.quantity} × {lot.lot_type.replace("_", " ")}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
